// src/components/invoice/InvoiceBreakdownTable.tsx
import React from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useParticipantLabel } from "../../../hooks/useParticipantLabel";
import { useTenantFormat } from "../../../hooks/useTenantFormat";
import { getPrimaryText } from "../../../utils/themeUtils";

type Props = {
  invoiceData: any;
  dueDate?: string;
};

const InvoiceBreakdownTable: React.FC<Props> = ({ invoiceData, dueDate }) => {
  const { isDarkMode } = useTheme();
  const participant = useParticipantLabel();
  const { formatCurrency } = useTenantFormat();
  const breakdown = invoiceData?.metadata?.calculateData?.slotBreakdown;
  if (!breakdown) return null;

  const groups = new Map();
  breakdown.forEach((slot: any) => {
    const athleteId =
      slot?.athleteId || slot?.summaryForAthlete || slot?.discountForAthlete;
    if (!groups.has(athleteId)) groups.set(athleteId, []);
    groups.get(athleteId).push(slot);
  });

  const rows: JSX.Element[] = [];

  groups.forEach((entries: any[]) => {
    const summary = entries.find((s) => s?.summaryForAthlete);
    const programs = entries.filter(
      (p) => !p.summaryForAthlete && !p.discountForAthlete
    );
    const discount = entries.find((d) => d.discountForAthlete);

    // ✅ Green summary row
    if (summary) {
      rows.push(
        <tr
          key={`summary-${summary.summaryForAthlete}`}
          className={`border-t font-semibold ${
            isDarkMode
              ? "bg-green-900 text-green-100"
              : "bg-green-100 text-green-900"
          }`}
        >
          <td className="p-2">{summary.athleteName}</td>
          <td className="p-2">
            {/* {dueDate
              ? new Date(dueDate).toLocaleDateString("en-GB", {
                  month: "short",
                })
              : "Billing Month"} */}
            Monthly (Prorated if Applicable)
          </td>
          {/* <td className="p-2">
            {tierNiceNames[summary.tierKey] ||
              summary.tierKey?.replace(/([a-z])([A-Z])/g, "$1 $2")}
          </td> */}
          <td className="p-2">{formatCurrency(summary.tierAmount || 0)}</td>
        </tr>
      );
    }

    // ⬜ Program rows
    programs.forEach((prog: any, idx: number) => {
      rows.push(
        <tr
          key={`program-${prog.slotId || idx}`}
          className={`${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          } border-t`}
        >
          <td className="p-2">-</td>
          <td className="p-2">{prog.programName || "-"}</td>
          {/* <td className="p-2">{tierNiceNames[prog.tierKey] || "-"}</td> */}
          <td className="p-2">
            {prog.tierAmount !== undefined
              ? formatCurrency(prog.tierAmount)
              : "-"}
          </td>
        </tr>
      );
    });

    // 🟡 Discount row
    if (discount) {
      rows.push(
        <tr
          key={`discount-${discount.discountForAthlete}`}
          className={`border-t ${
            isDarkMode
              ? "bg-yellow-900 text-yellow-100"
              : "bg-yellow-50 text-yellow-900"
          }`}
        >
          <td
            className={`p-2 font-semibold ${
              isDarkMode ? "text-orange-400" : "text-orange-700"
            }`}
            colSpan={4}
          >
            🎉 {discount.note}{" "}
            <span className="font-mono">{discount.discountForAthleteName}</span>{" "}
            — Discount: <strong>{formatCurrency(discount.discountAmount || 0)}</strong>
          </td>
        </tr>
      );
    }
  });

  // ✅ Final total
  rows.push(
    <tr
      key="final-total"
      className={`border-t font-bold ${
        isDarkMode
          ? "bg-green-900 text-green-100"
          : "bg-green-200 text-green-900"
      }`}
    >
      <td className="p-2"></td>
      <td className="p-2">Invoice Total</td>
      {/* <td className="p-2"></td> */}
      <td className="p-2">
        {formatCurrency(invoiceData?.metadata?.calculateData?.totalAmount || 0)}
      </td>
    </tr>
  );

  return (
    <div className="mb-6">
      <h2 className={`font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>
        Breakdown
      </h2>
      <table
        className={`w-full text-sm text-left mt-4 ${
          isDarkMode ? "border-gray-700" : "border-gray-300"
        } border`}
      >
        <thead
          className={`${
            isDarkMode
              ? "bg-gray-800 text-gray-200"
              : "bg-gray-100 text-gray-900"
          }`}
        >
          <tr>
            <th className="p-2">{participant.singular}</th>
            <th className="p-2">Description</th>
            {/* <th className="p-2">Tier</th> */}
            <th className="p-2">Amount</th>
          </tr>
        </thead>
        <tbody className={getPrimaryText(isDarkMode)}>{rows}</tbody>
      </table>
    </div>
  );
};

export default InvoiceBreakdownTable;
